import { RestartAlt } from "@mui/icons-material";
import { Box, Button, Dialog, DialogContent, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import {
	DateRange,
	StaticDateRangePicker,
	TimePicker,
	dateRangeCalendarClasses,
	pickersLayoutClasses,
	renderTimeViewClock
} from "@mui/x-date-pickers-pro";
import moment, { Moment } from "moment";
import { FC, useEffect, useState } from "react";

import { ICustomTimeRangeDetails } from ".";
import { TIME_RANGE_OPTIONS_E } from "../../types";

interface ICustomRangeSelectionDialogProps {
	open: boolean;
	defaultValues?: ICustomTimeRangeDetails;
	showPredefinedValues?: boolean;
	defaultPredefinedValue?: TIME_RANGE_OPTIONS_E;
	onClose: () => void;
	onSubmit: (updatedValue: ICustomTimeRangeDetails, timeRange: TIME_RANGE_OPTIONS_E) => void;
}

interface IPredefinedFilterDetails {
	key: TIME_RANGE_OPTIONS_E;
	name: string;
}

const PREDEFINED_FILTERS_LIST: IPredefinedFilterDetails[] = [
	{ key: TIME_RANGE_OPTIONS_E.TODAY, name: "Live / Today" },
	{ key: TIME_RANGE_OPTIONS_E.YESTERDAY, name: "Yesterday" },
	{ key: TIME_RANGE_OPTIONS_E.WEEK, name: "Last 7 Days" },
	{ key: TIME_RANGE_OPTIONS_E.MONTH, name: "Last Month" },
	{ key: TIME_RANGE_OPTIONS_E.QUARTER, name: "Last 3 Months" },
	{ key: TIME_RANGE_OPTIONS_E.HALF_YEAR, name: "Last 6 Months" }
];

const CustomRangeSelectionDialog: FC<ICustomRangeSelectionDialogProps> = (props) => {
	const { open, defaultValues, showPredefinedValues, defaultPredefinedValue, onClose, onSubmit } = props;

	const [predefinedRange, setPredefinedRange] = useState<TIME_RANGE_OPTIONS_E>(TIME_RANGE_OPTIONS_E.TODAY);
	const [customRange, setCustomRange] = useState<ICustomTimeRangeDetails>({
		from: moment().startOf("day"),
		to: moment().endOf("day")
	});

	function handleChangeDateRangeValues(value: DateRange<moment.Moment>) {
		setPredefinedRange(TIME_RANGE_OPTIONS_E.CUSTOM);

		setCustomRange((prev) => {
			const updatedValues = { ...prev };

			if (value[0]) {
				updatedValues.from = updatedValues.from
					.clone()
					.set("date", value[0].get("date"))
					.set("month", value[0].get("month"))
					.set("year", value[0].get("year"));
			}

			if (value[1]) {
				updatedValues.to = updatedValues.to
					.clone()
					.set("date", value[1].get("date"))
					.set("month", value[1].get("month"))
					.set("year", value[1].get("year"));
			}

			return updatedValues;
		});
	}

	function handleChangeTimeRangeValues(value: Moment, key: "from" | "to") {
		setPredefinedRange(TIME_RANGE_OPTIONS_E.CUSTOM);
		setCustomRange((prev) => ({
			...prev,
			[key]: prev[key].clone().set("hours", value.get("hours")).set("minutes", value.get("minutes")).set("seconds", 0)
		}));
	}

	function handleResetTimeRange() {
		setCustomRange((prev) => ({
			from: prev.from.clone().startOf("day"),
			to: prev.to.clone().endOf("day")
		}));
	}

	function handleApplyButtonClick() {
		onSubmit(customRange, predefinedRange);
	}

	function handlePredefinedDateValueSelection(updatedValue: TIME_RANGE_OPTIONS_E) {
		switch (updatedValue) {
			case TIME_RANGE_OPTIONS_E.TODAY:
				setCustomRange({
					from: moment().startOf("day"),
					to: moment().endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.YESTERDAY:
				setCustomRange({
					from: moment().subtract(1, "day").startOf("day"),
					to: moment().subtract(1, "day").endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.WEEK:
				setCustomRange({
					from: moment().subtract(1, "week").startOf("day"),
					to: moment().endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.MONTH:
				setCustomRange({
					from: moment().subtract(1, "month").startOf("day"),
					to: moment().endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.QUARTER:
				setCustomRange({
					from: moment().subtract(3, "months").startOf("day"),
					to: moment().endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.HALF_YEAR:
				setCustomRange({
					from: moment().subtract(6, "months").startOf("day"),
					to: moment().endOf("day")
				});
				break;

			default:
				break;
		}

		setPredefinedRange(updatedValue);
	}

	useEffect(() => {
		if (open) {
			if (defaultValues) setCustomRange(defaultValues);
			if (defaultPredefinedValue) setPredefinedRange(defaultPredefinedValue);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const startDateString = customRange.from.format("DD/MM/YYYY");
	const endDateString = customRange.to.format("DD/MM/YYYY");

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" classes={{ paper: "custom-range-dialog-paper" }}>
			<DialogContent classes={{ root: "dialog-content-root" }}>
				{showPredefinedValues ? (
					<Box className="predefined-date-value-selection-wrapper">
						{PREDEFINED_FILTERS_LIST.map((predefinedFilterItem) => (
							<Box
								key={predefinedFilterItem.key}
								className={`predefined-date-value ${predefinedRange === predefinedFilterItem.key ? "active" : ""}`}
								onClick={() => handlePredefinedDateValueSelection(predefinedFilterItem.key)}
							>
								<Typography variant="body2" fontWeight={500} textAlign='center'>
									{predefinedFilterItem.name}
								</Typography>
							</Box>
						))}
					</Box>
				) : null}

				<Typography
					variant="h6"
					sx={{
						marginTop: showPredefinedValues ? "16px" : "0",
						marginBottom: "12px",
						fontWeight: 600,
						color: "#2D2D2D"
					}}
				>
					Select Date Range
				</Typography>

				<Box className="date-range-toolbar-section-wrapper">
					<Box className="date-range-inputs-wrapper">
						<TextField label="From" value={startDateString} size="small" fullWidth />
						<TextField label="To" value={endDateString} size="small" fullWidth />
					</Box>
				</Box>

				<Box className="custom-time-range-wrapper">
					<Typography variant="h6" sx={{ fontWeight: 600, color: "#2D2D2D" }}>
						Select Time Range
					</Typography>

					<Tooltip
						disableInteractive
						title="Reset Time"
						placement="top"
						slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -14] } }] } }}
					>
						<IconButton
							size="small"
							onClick={handleResetTimeRange}
							sx={{
								backgroundColor: "rgba(91, 78, 191, 0.08)",
								color: "#5b4ebf",
								"&:hover": {
									backgroundColor: "rgba(91, 78, 191, 0.12)",
									transform: "scale(1.05)"
								},
								transition: "all 0.2s ease-in-out"
							}}
						>
							<RestartAlt fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>

				<Box className="date-range-toolbar-section-wrapper">
					<Box className="date-range-inputs-wrapper">
						<TimePicker
							label="From"
							value={customRange.from}
							onChange={(value) => handleChangeTimeRangeValues(value ?? moment().startOf("day"), "from")}
							closeOnSelect={false}
							viewRenderers={{
								hours: renderTimeViewClock,
								minutes: renderTimeViewClock
							}}
							slotProps={{ textField: { fullWidth: true, variant: "outlined", size: "small" } }}
						/>

						<TimePicker
							label="To"
							value={customRange.to}
							onChange={(value) => handleChangeTimeRangeValues(value ?? moment().endOf("day"), "to")}
							closeOnSelect={false}
							viewRenderers={{
								hours: renderTimeViewClock,
								minutes: renderTimeViewClock
							}}
							slotProps={{ textField: { fullWidth: true, variant: "outlined", size: "small" } }}
						/>
					</Box>
				</Box>

				<Box sx={{ marginY: "1rem" }}>
					<StaticDateRangePicker
						disableFuture
						disableHighlightToday
						disableAutoMonthSwitching
						calendars={1}
						value={[customRange.from, customRange.to]}
						onChange={handleChangeDateRangeValues}
						sx={{
							[`.${pickersLayoutClasses.contentWrapper}`]: {
								alignItems: "center"
							},
							[`.${dateRangeCalendarClasses.monthContainer}`]: {
								border: "1px solid rgba(91, 78, 191, 0.2)",
								borderRadius: "12px",
								boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)"
							}
						}}
						slots={{
							toolbar: () => <></>,
							actionBar: () => <></>
						}}
					/>
				</Box>

				<Box className="content-center">
					<Button variant="contained" color="primary" size="large" onClick={handleApplyButtonClick}>
						Apply
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default CustomRangeSelectionDialog;
